import { useEffect, useRef, useState } from "react";
import { axiosInstance } from "../../auth/Auth";
import ErrorResponse from "../ErrorResponse";
import { Col, Container, Modal, Row, Stack } from "react-bootstrap";
import LoadingIndicator from "../LoadingIndicator";
import useSortableData from "../useSortableData";
import { useVirtualizer } from "@tanstack/react-virtual";
import ListenerHistoryHeader from "./ListenerHistoryHeader";
import ListenerHistoryRow from "./ListenerHistoryRow";
import { GenderFemale, GenderMale, HouseFill, PersonFill, PersonSquare } from "react-bootstrap-icons";

export default function ListenerHistoryModal({ show, onHide, areaId, listener, endDatetime }) {
    const [loading, setLoading] = useState(true);
    const [errorCode, setErrorCode] = useState(null);
    const [data, setData] = useState(null);

    const tableContainerRef = useRef(null);

    useEffect(() => {
        async function fetchData() {
            setLoading(true);
            try {
                const input = {
                    area_id: areaId,
                    end_datetime: endDatetime,
                };
                const response = await axiosInstance.post(`listeners/${listener.listener_id}/history`, input);
                setErrorCode(null);
                setData(response.data);
            } catch (error) {
                setData(null);
                if (error?.code !== "ERR_CANCELED") setErrorCode(error?.response?.status ?? 500);
            }
            setLoading(false);
        }
        if (areaId && listener && endDatetime) fetchData();
    }, [areaId, endDatetime, listener]);

    const { sortedItems, requestSort, sortConfig } = useSortableData(data?.sessions, { key: "start_datetime", direction: "ascending" });

    const virtualizer = useVirtualizer({
        count: sortedItems?.length,
        estimateSize: () => 32,
        overscan: 30,
        getScrollElement: () => tableContainerRef.current,
    });

    return (
        <Modal show={show} onHide={onHide} size="xl" centered scrollable dialogClassName="modal-xxxl">
            <Modal.Header closeButton className=" py-1 py-xl-3">
                <Modal.Title className=" text-uppercase">Listener History</Modal.Title>
            </Modal.Header>
            <Modal.Body className="py-0 pb-1" ref={tableContainerRef}>
                {listener && (
                    <Row className=" align-items-center my-2">
                        <Col xs={"auto"}>
                            <PersonSquare width={60} height={60} />
                        </Col>
                        <Col>
                            <Row className="row-cols-1 row-cols-lg-2 row-cols-xl-4">
                                <Col>
                                    <Stack direction="horizontal" gap={1}>
                                        <span className="fw-bold">Age:</span>
                                        <span>{listener.age}</span>
                                    </Stack>
                                </Col>
                                <Col>
                                    <Stack direction="horizontal" gap={1}>
                                        <span className="fw-bold">Sex:</span>
                                        <span className=" d-flex align-items-center column-gap-1">
                                            {listener.sex === "Male" && <GenderMale className="text-ra-alternative-blue" height={18} width={18} title="Male" />}
                                            {listener.sex === "Female" && <GenderFemale className="text-ra-brown" height={18} width={18} title="Female" />}
                                            {listener.sex}
                                        </span>
                                    </Stack>
                                </Col>
                                {listener.personal_income && (
                                    <Col>
                                        <Stack direction="horizontal" gap={1}>
                                            <span className="fw-bold d-flex align-items-center">
                                                <PersonFill title="Personal" height={16} width={16} className="me-1" />
                                                Income:
                                            </span>
                                            <span>{listener.personal_income}</span>
                                        </Stack>
                                    </Col>
                                )}
                                {listener.household_income && (
                                    <Col>
                                        <Stack direction="horizontal" gap={1}>
                                            <span className="fw-bold d-flex align-items-center">
                                                <HouseFill title="Household" height={16} width={16} className="me-1" />
                                                Income:
                                            </span>
                                            <span>{listener.household_income}</span>
                                        </Stack>
                                    </Col>
                                )}
                                {listener.region && (
                                    <Col>
                                        <Stack direction="horizontal" gap={1}>
                                            <span className="fw-bold">Region:</span>
                                            <span>{listener.region}</span>
                                        </Stack>
                                    </Col>
                                )}
                                {listener.education && (
                                    <Col>
                                        <Stack direction="horizontal" gap={1}>
                                            <span className="fw-bold">Education:</span>
                                            <span>{listener.education}</span>
                                        </Stack>
                                    </Col>
                                )}
                            </Row>
                        </Col>
                    </Row>
                )}
                {!loading && errorCode && <ErrorResponse errorCode={errorCode} />}
                {loading && (
                    <span colSpan={12}>
                        <LoadingIndicator />
                    </span>
                )}
                {!loading && data && (
                    <>
                        <ListenerHistoryHeader sortConfig={sortConfig} requestSort={requestSort} />
                        <div>
                            <Container fluid className="position-relative" style={{ height: virtualizer.getTotalSize() }}>
                                {virtualizer.getVirtualItems().map(virtualRow => {
                                    const item = sortedItems[virtualRow.index];
                                    return <ListenerHistoryRow item={item} virtualRow={virtualRow} virtualizer={virtualizer} />;
                                })}
                                {!virtualizer.getVirtualItems().length && (
                                    <Row className="py-1 border-bottom">
                                        <Col className=" text-center">No Listeners</Col>
                                    </Row>
                                )}
                            </Container>
                        </div>
                    </>
                )}
            </Modal.Body>
        </Modal>
    );
}
